import React, { useState, useEffect, useCallback } from "react";
import { BuilderHeader } from "./components/BuilderHeader";
import { ComponentSelector } from "./components/ComponentSelector";
import { BuildSummary } from "./components/BuildSummary";
import { ProductSelectionModal } from "./components/ProductSelectionModal";
import {
  ComponentCategory,
  IProduct,
  WidgetConfig,
  ICompatibilityResponse,
} from "./types";
import { CATEGORIES } from "./utils/constants";
import { apiService } from "./services/api";
import { useBuildPersistence } from "./hooks/useBuildPersistence";

interface AppProps {
  config: WidgetConfig;
}

export const App: React.FC<AppProps> = ({ config }) => {
  const [selectedComponents, setSelectedComponents] = useState<
    Record<string, IProduct>
  >({});
  const [activeCategory, setActiveCategory] =
    useState<ComponentCategory | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [compatibilityData, setCompatibilityData] =
    useState<ICompatibilityResponse | null>(null);
  const [isCheckingCompatibility, setIsCheckingCompatibility] = useState(false);

  const creatorSlug = config.creatorSlug || "";

  // Handle rehydrated build from localStorage
  const handleRehydrated = useCallback(
    (components: Record<string, IProduct>) => {
      setSelectedComponents(components);
      if (config.onBuildChange) {
        config.onBuildChange(components);
      }
    },
    [config]
  );

  // Handle rehydration errors
  const handleRehydrationError = useCallback(
    (error: Error, missingProducts: string[]) => {
      console.warn(
        "[APP] Some products could not be restored:",
        missingProducts
      );
    },
    []
  );

  // Build persistence hook
  const { isRehydrating, saveBuild } = useBuildPersistence({
    creatorSlug,
    onRehydrated: handleRehydrated,
    onRehydrationError: handleRehydrationError,
  });

  // Auto-save build whenever it changes
  useEffect(() => {
    if (!isRehydrating) {
      saveBuild(selectedComponents);
    }
  }, [selectedComponents, isRehydrating, saveBuild]);

  const selectedCategoryIds = new Set(Object.keys(selectedComponents));

  useEffect(() => {
    const checkCompatibility = async () => {
      const wheel = selectedComponents["wheel"];
      const wheelBase = selectedComponents["wheel-base"];

      if (wheel && wheelBase) {
        setIsCheckingCompatibility(true);
        try {
          const result = await apiService.getCompatibility(
            wheelBase.brand.name,
            wheel.brand.name,
          );
          setCompatibilityData(result);
        } catch (error) {
          console.error("Failed to check compatibility:", error);
          setCompatibilityData(null);
        } finally {
          setIsCheckingCompatibility(false);
        }
      } else {
        setCompatibilityData(null);
      }
    };

    checkCompatibility();
  }, [selectedComponents]);

  const handleCategorySelect = (category: ComponentCategory) => {
    setActiveCategory(category);
    setModalOpen(true);
  };

  const handleSelectProduct = (product: IProduct) => {
    if (!activeCategory) return;

    const existingProduct = selectedComponents[activeCategory.id];

    // If replacing an existing product, track removal first
    if (existingProduct) {
      apiService.trackAnalytics({
        productId: existingProduct.id.toString(),
        productName: existingProduct.name,
        brandName: existingProduct.brand?.name || "Unknown",
        category: activeCategory.id,
        price: existingProduct.price || 0,
        currency: "USD",
        action: "removed",
      });
    }

    const newComponents = {
      ...selectedComponents,
      [activeCategory.id]: product,
    };

    setSelectedComponents(newComponents);

    // Track the new product addition
    apiService.trackAnalytics({
      productId: product.id.toString(),
      productName: product.name,
      brandName: product.brand?.name || "Unknown",
      category: activeCategory.id,
      price: product.price || 0,
      currency: "USD",
      action: "added",
    });

    if (config.onBuildChange) {
      config.onBuildChange(newComponents);
    }
  };

  const handleRemoveComponent = (categoryId: string) => {
    const removedProduct = selectedComponents[categoryId];

    if (removedProduct) {
      // Find the category name for tracking
      const category = CATEGORIES.find((cat) => cat.id === categoryId);

      apiService.trackAnalytics({
        productId: removedProduct.id.toString(),
        productName: removedProduct.name,
        brandName: removedProduct.brand?.name || "Unknown",
        category: categoryId,
        price: removedProduct.price || 0,
        currency: "USD",
        action: "removed",
      });
    }

    const newComponents = { ...selectedComponents };
    delete newComponents[categoryId];

    setSelectedComponents(newComponents);

    if (config.onBuildChange) {
      config.onBuildChange(newComponents);
    }
  };

  const handleClearAll = () => {
    setSelectedComponents({});

    if (config.onBuildChange) {
      config.onBuildChange({});
    }
  };

  // Handle loading a build from share code
  const handleLoadBuild = useCallback(
    (components: Record<string, IProduct>) => {
      setSelectedComponents(components);
      if (config.onBuildChange) {
        config.onBuildChange(components);
      }
    },
    [config]
  );

  return (
    <div className="srb-min-h-screen srb-bg-background srb-text-main">
      <div className="srb-mx-auto srb-p-4 md:srb-p-8 srb-bg-background">
        {/* <BuilderHeader
          onClearAll={handleClearAll}
          hasSelections={Object.keys(selectedComponents).length > 0}
        /> */}

        <div className="srb-grid srb-grid-cols-1 lg:srb-grid-cols-3 srb-gap-6 srb-mt-0">
          <div className="lg:srb-col-span-2">
            <ComponentSelector
              categories={CATEGORIES}
              onCategorySelect={handleCategorySelect}
              selectedCategories={selectedCategoryIds}
            />
          </div>

          <div className="lg:srb-col-span-1">
            <BuildSummary
              selectedComponents={selectedComponents}
              onRemoveComponent={handleRemoveComponent}
              compatibilityData={compatibilityData}
              isCheckingCompatibility={isCheckingCompatibility}
              onLoadBuild={handleLoadBuild}
            />
          </div>
        </div>
      </div>

      <ProductSelectionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        category={activeCategory}
        onSelectProduct={handleSelectProduct}
      />
    </div>
  );
};
